import { CallInputs, CallOutputs, NotificationOutputs } from "@EspaceParkQC/sdk-call-center";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Slider,
} from "@EspaceParkQC/sdk-react";
import { Bell, ChevronDown, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export default function Devices(): React.ReactElement {
  const { t } = useTranslation("main");
  return (
    <div className="flex justify-center xl:flex-row flex-col gap-2 p-4 border-b">
      {/* Call Input Menu */}
      <CallInputs
        render={({ devices, selectedDeviceId, selectDevice, isLoading, isMuted }) => {
          const selectedDevice = devices.find((device) => device.deviceId === selectedDeviceId);
          const selectedDeviceLabel = selectedDevice?.label || `Device ${selectedDeviceId}`;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between px-2" disabled={isLoading}>
                  <div className="flex items-center gap-2 flex-1">
                    <Mic className="size-4" />
                    <span className="truncate text-xs font-semibold">{selectedDeviceLabel}</span>
                  </div>
                  <Separator className="w-px h-full md:block hidden" />
                  <div className="flex items-center gap-2">{isMuted ? <MicOff className="size-4" /> : <Mic className="size-4" />}</div>
                  <ChevronDown className="size-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-90">
                <DropdownMenuLabel>{t("devices.call-input")}</DropdownMenuLabel>
                {devices.length > 0 ? (
                  devices.map((device) => (
                    <DropdownMenuItem
                      key={device.deviceId}
                      onClick={() => selectDevice(device.deviceId)}
                      className={cn("text-xs", device.deviceId === selectedDeviceId ? "bg-accent" : "")}
                    >
                      {device.label || `${t("devices.device")} ${device.deviceId}`}
                    </DropdownMenuItem>
                  ))
                ) : (
                  <DropdownMenuItem disabled className="text-xs">
                    {t("devices.no-microphones-detected")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }}
      />

      <CallOutputs
        render={({ devices, selectedDeviceId, selectDevice, volume, setVolume }) => {
          const selectedDevice = devices.find((device) => device.deviceId === selectedDeviceId);
          const selectedDeviceLabel = selectedDevice?.label || `Device ${selectedDeviceId}`;
          const [previousVolume, setPreviousVolume] = useState(volume);
          const isMuted = useMemo(() => volume === 0, [volume]);

          const mute = useCallback(() => {
            setPreviousVolume(volume);
            setVolume(0);
          }, [volume, setVolume]);

          const unmute = useCallback(() => {
            setVolume(previousVolume);
          }, [previousVolume, setVolume]);

          const toggleMute = useCallback(() => {
            if (isMuted) {
              unmute();
            } else {
              mute();
            }
          }, [isMuted, unmute, mute]);

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between px-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Bell className="size-4" />
                    <span className="truncate text-xs font-semibold">{selectedDeviceLabel}</span>
                  </div>
                  <Separator className="w-px h-full md:block hidden" />
                  <div className="flex items-center gap-2">
                    {isMuted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}{" "}
                    <span className="truncate text-xs font-semibold">{Math.round(volume * 100)}%</span>
                  </div>
                  <ChevronDown className="size-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-90">
                <DropdownMenuLabel>{t("devices.call-output")}</DropdownMenuLabel>

                {devices.map((device) => (
                  <DropdownMenuItem
                    key={device.deviceId}
                    onClick={() => selectDevice(device.deviceId)}
                    className={cn("text-xs", device.deviceId === selectedDeviceId ? "bg-accent" : "")}
                  >
                    {device.label || `${t("devices.device")} ${device.deviceId}`}
                  </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator />

                <div className="flex items-center gap-2 p-3">
                  <Button size="sm" variant="outline" className="justify-between" onClick={toggleMute}>
                    {isMuted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
                  </Button>
                  <span className="truncate text-xs font-semibold">{Math.round(volume * 100)}%</span>
                  <div className="flex-1">
                    <Slider value={[volume]} onValueChange={(value) => setVolume(value[0])} max={1} min={0} step={0.01} />
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }}
      />

      <NotificationOutputs
        render={({ devices, selectedDeviceId, selectDevice, volume, setVolume }) => {
          const selectedDevice = devices.find((device) => device.deviceId === selectedDeviceId);
          const selectedDeviceLabel = selectedDevice?.label || `Device ${selectedDeviceId}`;
          const [previousVolume, setPreviousVolume] = useState(volume);
          const isMuted = useMemo(() => volume === 0, [volume]);

          const mute = useCallback(() => {
            setPreviousVolume(volume);
            setVolume(0);
          }, [volume, setVolume]);

          const unmute = useCallback(() => {
            setVolume(previousVolume);
          }, [previousVolume, setVolume]);

          const toggleMute = useCallback(() => {
            if (isMuted) {
              unmute();
            } else {
              mute();
            }
          }, [isMuted, unmute, mute]);

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="justify-between px-2">
                  <div className="flex items-center gap-2 flex-1">
                    <Bell className="size-4" />
                    <span className="truncate text-xs font-semibold">{selectedDeviceLabel}</span>
                  </div>
                  <Separator className="w-px h-full md:block hidden" />
                  <div className="flex items-center gap-2">
                    {isMuted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}{" "}
                    <span className="truncate text-xs font-semibold">{Math.round(volume * 100)}%</span>
                  </div>
                  <ChevronDown className="size-4 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-90">
                <DropdownMenuLabel>{t("devices.notification-output")}</DropdownMenuLabel>

                {devices.map((device) => (
                  <DropdownMenuItem
                    key={device.deviceId}
                    onClick={() => selectDevice(device.deviceId)}
                    className={cn("text-xs", device.deviceId === selectedDeviceId ? "bg-accent" : "")}
                  >
                    {device.label || `${t("devices.device")} ${device.deviceId}`}
                  </DropdownMenuItem>
                ))}

                <DropdownMenuSeparator />

                <div className="flex items-center gap-2 p-3">
                  <Button size="sm" variant="outline" className="justify-between" onClick={toggleMute}>
                    {isMuted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
                  </Button>
                  <span className="truncate text-xs font-semibold">{Math.round(volume * 100)}%</span>
                  <div className="flex-1">
                    <Slider value={[volume]} onValueChange={(value) => setVolume(value[0])} max={1} min={0} step={0.01} />
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        }}
      />
    </div>
  );
}
